/**
 * Auth posture per build — the production auth model (#116 / locked auth model).
 *
 * The demo/internal build stays god-mode (self-signup + password + demo auto
 * sign-in). A client PRODUCT build (a fork, VITE_SURFACE=product) follows the
 * locked model: enterprise SSO, users provisioned by the IdP / admin import,
 * and NO self-service signup. This module centralizes "what can a user do at the
 * auth wall on this build" so the LoginForm + authStore agree in one place.
 *
 * Intentionally separate from productSurface (which answers "is the dev cockpit
 * visible") — this answers "what auth actions are permitted". It builds ON the
 * surface: product → locked-down by default, overridable per fork via env.
 */
import { isProductSurface } from './productSurface'

const env = import.meta.env as Record<string, string | undefined>

/**
 * Self-service account creation. OFF on a product/client build — forks provision
 * users via SSO JIT or admin/org import, never self-signup (the locked model).
 * Internal/demo keeps it on. A fork can force the choice with
 * VITE_ALLOW_SELF_SIGNUP=true|false; unset falls back to the surface.
 */
export function isSelfSignupAllowed(): boolean {
  if (env.VITE_ALLOW_SELF_SIGNUP === 'true') return true
  if (env.VITE_ALLOW_SELF_SIGNUP === 'false') return false
  return !isProductSurface()
}

/**
 * Whether the email/password sign-in path is offered. On by default; a fork that
 * is SSO-only can turn it off (VITE_PASSWORD_LOGIN=false) so the wall shows only
 * "Sign in with SSO" (Slice 2 wires the button). Demo/internal always keeps it.
 */
export function isPasswordLoginAllowed(): boolean {
  if (env.VITE_PASSWORD_LOGIN === 'false' && isProductSurface()) return false
  return true
}

/**
 * The IdP domain a fork's SSO is registered under (Supabase SAML SSO resolves the
 * provider by email domain). A fork sets VITE_SSO_DOMAIN (e.g. 'codan.com'); the
 * actual SAML provider is configured in that fork's Supabase project.
 */
export function ssoDomain(): string | null {
  return env.VITE_SSO_DOMAIN ?? null
}

/**
 * Whether to offer "Sign in with SSO". PRODUCT builds only — the demo/internal
 * cockpit never uses SSO (it's god-mode) — and only when a domain is configured.
 * So the seam is inert on the demo and lights up on a fork that sets VITE_SSO_DOMAIN.
 */
export function isSsoLoginEnabled(): boolean {
  return !!ssoDomain() && isProductSurface()
}

/**
 * SSO JIT policy (#116 / locked auth model) — what happens when someone
 * authenticates (SSO or password) but has NO `public.users` roster row (they
 * weren't in the SuccessFactors / admin import):
 *   'require_roster' (default, safest for ITAR): DENY — only pre-provisioned
 *     people get access; the login lands on "contact your administrator".
 *   'provision': JIT-create a roster row from the auth/SSO claims — a fork that
 *     wants self-provisioning wires the backend seam (needs the IdP's claim →
 *     org/role mapping, so it's a fork-time extension; safe-denies until wired).
 * Enforced only on a PRODUCT build (see the App bootstrap); internal/sandbox keeps
 * the metadata fallback for convenience. A fork sets VITE_SSO_JIT_MODE.
 */
export type SsoJitMode = 'require_roster' | 'provision'
export function ssoJitMode(): SsoJitMode {
  return env.VITE_SSO_JIT_MODE === 'provision' ? 'provision' : 'require_roster'
}
